import * as R from 'Router/routes';
import { AsyncFAQ } from 'Router/asyncRoutes';
import { ClientDebtRestructuringData } from 'hooks/webapi/useClientDebtRestructuring';
import { LatestLoan } from 'hooks/webapi/useLatestLoan';
import { useActiveLoan } from 'pages/ActiveLoan/useActiveLoan';
import { useClient, useLatestLoan } from './webapi';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CalendarIcon from 'assets/icons/calendar_menu.svg?react';
import DashboardIcon from 'assets/icons/dashboard.svg?react';
import HelpIcon from 'assets/icons/question_mark.svg?react';
import LearnVivusSelectedIcon from 'assets/icons/bundle/learnVivus-selected.svg?react';
import LearnVivusUnselectedIcon from 'assets/icons/bundle/learnVivus-unselected.svg?react';
import MoneyIcon from 'assets/icons/money.svg?react';
import ProfileIcon from 'assets/icons/profile.svg?react';
import RepaymentIcon from 'assets/icons/repayment.svg?react';
import useApplicationProposals from './webapi/useApplicationProposals';
import useBundle from 'hooks/useBundle';
import useClientDebtRestructuring from './webapi/useClientDebtRestructuring';
import VivusplusIcon from 'assets/icons/bundle/vivusPlus.svg?react';
import VivusplusUnselectedIcon from 'assets/icons/bundle/vivusPlus-unselected.svg?react';

function preloadFAQ() {
  AsyncFAQ.preload();
}

type Props = {
  menuDisabled?: boolean;
  shouldFetchMenu?: boolean;
};

export const isRepaymentRouteDisabled = (
  latestLoan: LatestLoan | null | undefined,
  debtRestructuringData: ClientDebtRestructuringData | undefined | null,
) => {
  return (
    !latestLoan ||
    latestLoan?.debtCollectLoanAgreement ||
    debtRestructuringData?.status === 'ACTIVE'
  );
};

const useMenuItems = (
  { menuDisabled }: Props = {
    menuDisabled: false,
  },
) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { latestLoan } = useLatestLoan();
  const { isOverdue, isBrokenAgreement } = useActiveLoan();
  const { applicationProposals } = useApplicationProposals();
  const { debtRestructuringData } = useClientDebtRestructuring();
  const { client } = useClient();
  const { isBundleAccepted } = useBundle();

  const repaymentUrl =
    isOverdue && !isBrokenAgreement ? R.FULL_REPAYMENT : R.REPAYMENT;

  const menuItems = useMemo(() => {
    const menuList = [
      {
        title: t('menu:dashboard'),
        url: latestLoan ? R.ACTIVE_LOAN : R.REPEATED_LOAN,
        icon: <DashboardIcon />,
        dataTest: 'dashboard',
        disabled: menuDisabled,
      },
      {
        title: t('menu:additional_amount'),
        url: R.ADDITIONAL_AMOUNT,
        icon: <MoneyIcon />,
        disabled:
          menuDisabled ||
          !client?.additionalAmount ||
          latestLoan?.debtCollectLoanAgreement,
        dataTest: 'additional-amount',
      },
      ...(latestLoan !== null && latestLoan?.productCode === 'MINI'
        ? [
            {
              title: t('menu:extention'),
              url: `${R.EXTENSIONS}?PID=${latestLoan?.productNumber}`,
              disabled:
                menuDisabled ||
                !latestLoan?.isExtensionPossible ||
                latestLoan?.debtCollectLoanAgreement,
              icon: <CalendarIcon />,
              dataTest: 'extention',
            },
          ]
        : []),
      {
        title: t('menu:repayment'),
        url: repaymentUrl,
        icon: <RepaymentIcon />,
        disabled:
          menuDisabled ||
          isRepaymentRouteDisabled(latestLoan, debtRestructuringData),
        dataTest: 'repayment',
      },
      {
        title: t('menu:help'),
        url: R.FAQ,
        icon: <HelpIcon />,
        onMouseEnter: { preloadFAQ },
        dataTest: 'faq',
        disabled: menuDisabled,
      },
      {
        disabled: menuDisabled,
        title: t('menu:profile'),
        url: R.PROFILE,
        icon: <ProfileIcon />,
        dataTest: 'profile',
      },
    ];

    if (isBundleAccepted) {
      const isLearnVivusSelected = pathname === R.BUNDLE_VIVUSPLUS_FAQ;
      menuList.splice(5, 0, {
        disabled: false,
        title: t('menu:learn_vivus_plus'),
        url: R.BUNDLE_VIVUSPLUS_FAQ,
        icon: isLearnVivusSelected ? (
          <LearnVivusSelectedIcon />
        ) : (
          <LearnVivusUnselectedIcon />
        ),
        dataTest: 'sidebar-vivusplus-learning',
      });

      const isVivusPlusActive = pathname === R.BUNDLE_VIVUSPLUS_BENEFITS;
      menuList.push({
        disabled: false,
        title: t('menu:vivus_plus'),
        url: R.BUNDLE_VIVUSPLUS_BENEFITS,
        icon: isVivusPlusActive ? (
          <VivusplusIcon />
        ) : (
          <VivusplusUnselectedIcon />
        ),
        dataTest: 'sidebar-vivusplus-benefits',
      });
    }
    return menuList;
  }, [
    applicationProposals,
    client?.additionalAmount,
    latestLoan,
    menuDisabled,
  ]);

  return menuItems;
};

export default useMenuItems;
